/**
 * NOS-158/NOS-161: assembles the theme gallery — the built-in default
 * package, the 10 art-directed archetype packages shipped in-repo
 * (src/themes/catalog/*, designed from docs/specs/NONPROFIT_SITE_RESEARCH.md
 * §B), and any active packages from the `templates` table (designer/third-
 * party themes land there later). The NOS-111 color-chip presets are retired
 * — their role is absorbed by the catalog.
 */
import { fetchActiveThemes, validateThemePackage } from "@/lib/themePackages"
import { getDefaultThemePackage } from "@/lib/seedPages"
import type { ThemePackage } from "@/types/theme"

export interface GalleryTheme {
  /** Stable id: `builtin:*`, `catalog:<slug>`, or the DB row uuid. */
  id: string
  name: string
  description: string
  cause_areas: string[]
  preview_image: string | null
  package: ThemePackage
  /** DB row id when the theme lives in the templates table (persisted as template_id). */
  templateId?: string
  source: "builtin" | "catalog" | "db"
}

export const DEFAULT_GALLERY_THEME_ID = "builtin:default"

/** In-repo archetype packages (NOS-161). Invalid packages throw at load — the catalog test guards this. */
const CATALOG_MODULES = import.meta.glob("../themes/catalog/*.json", { eager: true }) as Record<
  string,
  { default?: unknown }
>

function toGalleryTheme(id: string, pkg: ThemePackage, source: GalleryTheme["source"]): GalleryTheme {
  return {
    id,
    name: pkg.meta.name,
    description: pkg.meta.description ?? "",
    cause_areas: pkg.meta.cause_areas ?? [],
    preview_image: pkg.meta.preview_image ?? null,
    package: pkg,
    source,
  }
}

/** The shipped catalog, validated. Exported for the catalog test. */
export function getCatalogThemes(): GalleryTheme[] {
  return Object.entries(CATALOG_MODULES)
    .map(([path, mod]) => {
      const slug = path.split("/").pop()!.replace(/\.json$/, "")
      const result = validateThemePackage(mod.default ?? mod)
      if (!result.ok) {
        throw new Error(`Catalog theme "${slug}" is invalid: ${result.errors.join("; ")}`)
      }
      return toGalleryTheme(`catalog:${slug}`, result.pkg, "catalog")
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Built-ins are synchronous; use this when the DB list hasn't loaded yet. */
export function getBuiltinGalleryThemes(): GalleryTheme[] {
  return [
    toGalleryTheme(DEFAULT_GALLERY_THEME_ID, getDefaultThemePackage(), "builtin"),
    ...getCatalogThemes(),
  ]
}

/** Full gallery: built-in default + shipped catalog + active DB themes. */
export async function getGalleryThemes(): Promise<GalleryTheme[]> {
  const db = await fetchActiveThemes()
  return [
    ...getBuiltinGalleryThemes(),
    ...db.map((row) => ({
      ...toGalleryTheme(row.id, row.package, "db" as const),
      name: row.name,
      cause_areas: row.cause_areas,
      preview_image: row.preview_image_url ?? row.package.meta.preview_image ?? null,
      templateId: row.id,
    })),
  ]
}
